import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { saveCurrentPlaylist } from '../../store/player_favorites/favoritesService';

// "Save playlist" pill on /player/events/video — takes the marker IDs from
// whatever the player is currently watching and writes them to a new entry
// in /users/playlists (visible in /player/favorites afterwards). Uses a
// quick window.prompt for the title rather than a custom modal — keeps
// scope tight; a richer flow can replace it without changing the data path.
const SavePlaylistButton = ({ markerIds, defaultTitle }) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState(null); // null | 'saving' | 'saved' | 'failed'

  const onClick = useCallback(async () => {
    if (status === 'saving') return;
    const title = window.prompt('Save playlist as…', defaultTitle || 'My playlist');
    if (!title) return;
    const trimmed = title.trim();
    if (!trimmed) return;
    if (!Array.isArray(markerIds) || markerIds.length === 0) {
      setStatus('failed');
      setTimeout(() => setStatus(null), 2000);
      return;
    }
    setStatus('saving');
    const result = await dispatch(saveCurrentPlaylist({ title: trimmed, markerIds }));
    const ok = result.meta.requestStatus === 'fulfilled';
    setStatus(ok ? 'saved' : 'failed');
    setTimeout(() => setStatus(null), 2000);
  }, [dispatch, markerIds, defaultTitle, status]);

  return (
    <Wrap>
      <Button type="button" onClick={onClick} disabled={status === 'saving'}>
        {status === 'saving' ? 'Saving…' : '★ Save'}
      </Button>
      {status === 'saved' && <Toast role="status">Saved to Favorites</Toast>}
      {status === 'failed' && <Toast $error role="status">Save failed</Toast>}
    </Wrap>
  );
};

export default SavePlaylistButton;

const Wrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background: transparent;
  color: ${(props) => props.theme.white};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;

  &:hover:not(:disabled) { border-color: ${(props) => props.theme.blueish2}; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const Toast = styled.span`
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  padding: 4px 10px;
  border-radius: 999px;
  background: ${(props) => (props.$error ? '#3a1414' : props.theme.green)};
  color: ${(props) => (props.$error ? props.theme.white : props.theme.black)};
  font-family: 'SatoshiBold', sans-serif;
  font-size: 11px;
  letter-spacing: 0.3px;
  white-space: nowrap;
  pointer-events: none;
  animation: fadeIn 120ms ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-2px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;
